import { gql } from '@apollo/client'
import { useSubgraphClient } from 'index'
import { useEffect } from 'react'
import { useSetSnapshotData, useSnapshotState } from 'state/islandSnapshots/hooks/useSetSnapshotData'
import { parseUnits } from 'viem'

export const V2_SNAPSHOT_QUERY = gql`
  query GetV2PairDayData($poolId: ID!) {
    pairDayDatas(where: { pairAddress: $poolId }, orderBy: date, orderDirection: desc, first: 1000) {
      id
      date
      token0 {
        id
        symbol
        decimals
        derivedETH
      }
      token1 {
        id
        symbol
        decimals
        derivedETH
      }
      reserve0
      reserve1
      reserveUSD
      dailyVolumeToken0
      dailyVolumeToken1
      dailyVolumeUSD
      dailyTxns
    }
  }
`

function transformV2DataToSnapshot(pairDayData: any) {
  // Calculate token prices using derivedETH
  const token0Price = parseFloat(pairDayData.token0.derivedETH || '0')
  const token1Price = parseFloat(pairDayData.token1.derivedETH || '0')
  const token0Decimals = parseInt(pairDayData.token0.decimals)
  const token1Decimals = parseInt(pairDayData.token1.decimals)

  return {
    id: pairDayData.id,
    timestamp: pairDayData.date,
    totalValueLockedUSD: pairDayData.reserveUSD,
    _token0Amount: parseUnits(pairDayData.reserve0, token0Decimals).toString(),
    _token1Amount: parseUnits(pairDayData.reserve1, token1Decimals).toString(),
    volumeUSD: pairDayData.dailyVolumeUSD,
    apr: (
      ((parseFloat(pairDayData.dailyVolumeUSD) * 0.003 * 365) / parseFloat(pairDayData.reserveUSD)) *
      100
    ).toString(),
    vault: {
      apr: {
        averageApr: (
          ((parseFloat(pairDayData.dailyVolumeUSD) * 0.003 * 365) / parseFloat(pairDayData.reserveUSD)) *
          100
        ).toString(),
      },
      pool: {
        volumeUSD: pairDayData.dailyVolumeUSD,
        volumeToken0: pairDayData.dailyVolumeToken0,
        volumeToken1: pairDayData.dailyVolumeToken1,
      },
      _token0: {
        id: pairDayData.token0.id,
        symbol: pairDayData.token0.symbol,
        volume: parseUnits(pairDayData.dailyVolumeToken0, token0Decimals).toString(),
        volumeUSD: (parseFloat(pairDayData.dailyVolumeToken0) * token0Price).toString(),
        totalValueLocked: parseUnits(pairDayData.reserve0, token0Decimals).toString(),
        totalValueLockedUSD: (parseFloat(pairDayData.reserve0) * token0Price).toString(),
      },
      _token1: {
        id: pairDayData.token1.id,
        symbol: pairDayData.token1.symbol,
        volume: parseUnits(pairDayData.dailyVolumeToken1, token1Decimals).toString(),
        volumeUSD: (parseFloat(pairDayData.dailyVolumeToken1) * token1Price).toString(),
        totalValueLocked: parseUnits(pairDayData.reserve1, token1Decimals).toString(),
        totalValueLockedUSD: (parseFloat(pairDayData.reserve1) * token1Price).toString(),
      },
    },
  }
}

export const useV2SubgraphSnapshots = (poolAddress: string) => {
  const apolloClient = useSubgraphClient('v2')
  const { setSnapshots, setLoading } = useSetSnapshotData()

  useEffect(() => {
    setLoading(poolAddress, true)

    apolloClient
      .query({
        query: V2_SNAPSHOT_QUERY,
        variables: { poolId: poolAddress },
      })
      .then((response) => {
        if (response.data && response.data.pairDayDatas) {
          setSnapshots(poolAddress, response.data.pairDayDatas.map(transformV2DataToSnapshot))
        }
        setLoading(poolAddress, false)
      })
      .catch((error) => {
        console.error('Error fetching snapshots:', error)
        setLoading(poolAddress, false)
      })
  }, [apolloClient, poolAddress])

  return useSnapshotState(poolAddress)
}

export { transformV2DataToSnapshot }
